/**
 * StockEntryActionsSection — lifecycle-aware action card for a Stock Entry.
 * Rendered as the last card in the Shopify-style detail view so the primary
 * action is reachable without hunting through the header toolbar.
 *
 * Uses the `onSave` / `onSubmit` / `isSaving` props already exposed by
 * ShopifySectionWidgetProps — NO modification to StandardDetailPage.tsx
 * (which is already at 876 lines and over the TS soft-split threshold).
 *
 * ERPNext Stock Entry lifecycle:
 *   - docstatus 0 (Draft)    → Save + Submit (primary) — submit moves stock
 *   - docstatus 1 (Submitted) → Cancel (requires confirm) — reverses the entry
 *   - docstatus 2 (Cancelled) → Amend (user creates a new doc via standard flow)
 *
 * Amend on a cancelled doc is handled by ERPNext's standard "Amend" button
 * (it creates a new Draft doc). We don't replicate that flow here; instead
 * we surface a clear explanation so the user knows what to do next.
 */

import { useState } from "react";
import { AlertTriangle, CheckCircle2, Save, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/i18n/useLanguage";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";

const i18n: Record<string, { en: string; th: string }> = {
  actions: { en: "Actions", th: "การดำเนินการ" },
  save_draft: { en: "Save draft", th: "บันทึกแบบร่าง" },
  submit: { en: "Submit entry", th: "ส่งยืนยัน" },
  submit_hint: {
    en: "Submitting will post this stock movement and cannot be undone without cancelling.",
    th: "เมื่อส่งยืนยันแล้วสต็อกจะถูกเคลื่อนไหวจริงและต้องยกเลิกเอกสารเพื่อย้อนกลับ",
  },
  cancel_entry: { en: "Cancel entry", th: "ยกเลิกเอกสาร" },
  cancel_confirm_title: { en: "Cancel this entry?", th: "ยืนยันยกเลิกเอกสาร?" },
  cancel_confirm_hint: {
    en: "Cancelling will reverse the stock movement. This cannot be undone.",
    th: "การยกเลิกจะย้อนการเคลื่อนไหวสต็อก ไม่สามารถกู้คืนได้",
  },
  confirm_cancel: { en: "Yes, cancel", th: "ยืนยันยกเลิก" },
  keep_entry: { en: "Keep entry", th: "ย้อนกลับ" },
  submitted_label: { en: "Entry is submitted", th: "ส่งยืนยันแล้ว" },
  submitted_hint: {
    en: "Stock movement has been posted. Cancel this entry to reverse it.",
    th: "สต็อกถูกเคลื่อนไหวเรียบร้อย หากต้องการย้อนกลับให้ยกเลิกเอกสาร",
  },
  cancelled_label: { en: "Entry is cancelled", th: "เอกสารถูกยกเลิก" },
  cancelled_hint: {
    en: "This entry has been reversed. Use the Amend button in the page header to create a new draft from this one.",
    th: "เอกสารนี้ถูกย้อนกลับแล้ว กด Amend ด้านบนเพื่อสร้างแบบร่างใหม่จากเอกสารนี้",
  },
  saving: { en: "Saving…", th: "กำลังบันทึก…" },
  new_entry: { en: "New entry", th: "เอกสารใหม่" },
  new_entry_hint: {
    en: "Fill in the details and save to create this stock entry.",
    th: "กรอกรายละเอียดและบันทึกเพื่อสร้างเอกสาร",
  },
};

export function StockEntryActionsSection({
  formData,
  onSave,
  onSubmit,
  isSaving,
  isNew,
}: ShopifySectionWidgetProps) {
  const { language } = useLanguage();
  const t = (key: string) => {
    const entry = i18n[key];
    return entry ? (language === "th" ? entry.th : entry.en) : key;
  };

  const [confirmCancel, setConfirmCancel] = useState(false);
  const docstatus = Number(formData.docstatus ?? 0);

  const handleSubmit = async () => {
    if (!onSubmit) return;
    // Pass empty patch — any buffered edits are already flushed by the
    // parent form before onSubmit fires.
    await onSubmit({});
  };

  const handleCancelEntry = async () => {
    if (!onSubmit) return;
    // docstatus = 2 tells the backend to cancel the submitted doc
    await onSubmit({ docstatus: 2 });
    setConfirmCancel(false);
  };

  // New doc — show a friendly placeholder. Save happens through the form
  // auto-save, so no explicit button is needed until the first save.
  if (isNew) {
    return (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
          {t("actions")}
        </h3>
        <Card className={CARD}>
          <CardContent className="p-5">
            <div className="text-sm font-medium text-foreground">{t("new_entry")}</div>
            <div className="text-xs text-muted-foreground mt-1">{t("new_entry_hint")}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Draft — Save + Submit
  if (docstatus === 0) {
    return (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
          {t("actions")}
        </h3>
        <Card className={CARD}>
          <CardContent className="p-5 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {onSave ? (
                <Button
                  variant="outline"
                  onClick={onSave}
                  disabled={isSaving}
                  className="rounded-xl"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? t("saving") : t("save_draft")}
                </Button>
              ) : null}
              {onSubmit ? (
                <Button
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="rounded-xl"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {t("submit")}
                </Button>
              ) : null}
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{t("submit_hint")}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Submitted — Cancel with confirmation
  if (docstatus === 1) {
    return (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
          {t("actions")}
        </h3>
        <Card className={CARD}>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-start gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                <CheckCircle2 className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground">
                  {t("submitted_label")}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {t("submitted_hint")}
                </div>
              </div>
            </div>

            {confirmCancel ? (
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground">
                      {t("cancel_confirm_title")}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {t("cancel_confirm_hint")}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="destructive"
                    onClick={handleCancelEntry}
                    disabled={isSaving}
                    className="rounded-xl"
                  >
                    <XCircle className="h-4 w-4" />
                    {t("confirm_cancel")}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setConfirmCancel(false)}
                    disabled={isSaving}
                    className="rounded-xl"
                  >
                    {t("keep_entry")}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Button
                  variant="outline"
                  onClick={() => setConfirmCancel(true)}
                  disabled={isSaving || !onSubmit}
                  className="rounded-xl text-destructive hover:text-destructive"
                >
                  <XCircle className="h-4 w-4" />
                  {t("cancel_entry")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Cancelled — explanation only. Amend flow lives in the page header.
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
        {t("actions")}
      </h3>
      <Card className={CARD}>
        <CardContent className="p-5">
          <div className="flex items-start gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <XCircle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">
                {t("cancelled_label")}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t("cancelled_hint")}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
